/**
 * Shared lightmap apply logic for editor and runtime.
 */
import * as THREE from 'three';
import { EXRLoader } from 'three/addons/loaders/EXRLoader.js';
import { HDRLoader } from 'three/addons/loaders/HDRLoader.js';

import { MeshComponent } from '../components/visual/MeshComponent.js';
import { ModelMeshComponent } from '../components/visual/ModelMeshComponent.js';

import {
  buildDataZipAssetPathForScene,
  lightmapFormatFromPath,
  loadLightmapBuildData,
} from './lightmapBuildData.js';

import type { LightmapBuildData } from './lightmapBuildData.js';
import type { Actor } from '../actors/Actor.js';
import type { SceneComponent } from '../components/SceneComponent.js';
import type { World } from '../game/World.js';

const textureLoader = new THREE.TextureLoader();
const exrLoader = new EXRLoader();
const hdrLoader = new HDRLoader();

/** Dedupes shared atlas paths and loads one lightmap texture at a time per apply. */
class SharedLightmapTextures {
  private readonly cache = new Map<string, Promise<THREE.Texture>>();

  private loadTail: Promise<unknown> = Promise.resolve();

  load(buildData: LightmapBuildData, relativePath: string): Promise<THREE.Texture> {
    const key = relativePath.replace(/\\/g, '/');
    const cached = this.cache.get(key);
    if (cached) {
      return cached;
    }

    const pending = this.enqueueLoad(() => this.loadUncached(buildData, relativePath));
    this.cache.set(key, pending);
    pending.catch(() => this.cache.delete(key));
    return pending;
  }

  private enqueueLoad<T>(task: () => Promise<T>): Promise<T> {
    const result = this.loadTail.then(task);
    this.loadTail = result.then(
      () => undefined,
      () => undefined,
    );
    return result;
  }

  private async loadUncached(buildData: LightmapBuildData, relativePath: string): Promise<THREE.Texture> {
    const url = buildData.getLightmapBlobUrl(relativePath);
    const format = lightmapFormatFromPath(relativePath);

    if (format === 'exr') {
      return exrLoader.loadAsync(url);
    }
    if (format === 'hdr') {
      return hdrLoader.loadAsync(url);
    }
    return textureLoader.loadAsync(url);
  }
}

/** Stable manifest / export id from the scene component UUID. */
export function lightmapperObjectId(component: SceneComponent): string {
  return component.uuid;
}

export interface LightmapManifestEntry {
  id: string;
  lightMap: string;
  uv2: string;
  lightMapIntensity?: number;
  uvAttribute?: string;
  seamVertexCount?: number;
  maxSeamSpread?: number;
}

export interface LightmapManifest {
  lightmapResolution?: number;
  uvChannel?: number;
  objects: LightmapManifestEntry[];
}

export interface LightmapManifestApplyResult {
  applied: string[];
  skipped: Array<{ id: string; reason: string }>;
}

type LightmapCapableMaterial = THREE.Material & {
  lightMap: THREE.Texture | null;
  lightMapIntensity: number;
  needsUpdate: boolean;
};

function isLightmapCapableMaterial(material: THREE.Material): material is LightmapCapableMaterial {
  return 'lightMap' in material && 'lightMapIntensity' in material;
}

function meshSupportsLightmap(mesh: THREE.Mesh): boolean {
  const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  return materials.length > 0 && materials.every((material) => isLightmapCapableMaterial(material));
}

/**
 * flipY must follow the loader that produced the texture, not a single global default.
 * EXRLoader → HalfFloat DataTexture with bottom-first row order (flipY=false at load).
 * TextureLoader (PNG/JPEG) and HDRLoader → top-first ImageBitmap/DataTexture (flipY=true).
 * Applying flipY=true to EXR double-flips relative to uv2; leaving flipY=false on PNG/HDR misses the upload flip.
 */
function lightmapFlipYForPath(relativePath: string): boolean {
  return lightmapFormatFromPath(relativePath) !== 'exr';
}

export async function applyLightmap(
  mesh: THREE.Mesh,
  buildData: LightmapBuildData,
  entry: LightmapManifestEntry,
  textures: SharedLightmapTextures,
): Promise<void> {
  const uv2 = buildData.getUv2Data(entry.uv2);
  const vertexCount = mesh.geometry.attributes.position.count;
  if (vertexCount !== uv2.vertexCount) {
    throw new Error(
      `${mesh.name} uv2 mismatch: geometry has ${vertexCount} vertices, uv2 sidecar has ${uv2.vertexCount}`,
    );
  }

  mesh.geometry.setAttribute('uv2', new THREE.Float32BufferAttribute(uv2.uv, 2));

  if (entry.seamVertexCount && entry.seamVertexCount > 0) {
    console.warn(
      `[lightmapApply] ${mesh.name}: ${entry.seamVertexCount}/${uv2.vertexCount} vertices straddle ` +
        `lightmap seams (max spread ${entry.maxSeamSpread?.toFixed(4) ?? '?'}). uv2 was averaged; ` +
        'consider splitting seams in the source mesh to remove the smear.',
    );
  }

  const lightMap = await textures.load(buildData, entry.lightMap);
  lightMap.flipY = lightmapFlipYForPath(entry.lightMap);
  lightMap.channel = 2;
  lightMap.colorSpace = THREE.NoColorSpace;
  lightMap.needsUpdate = true;

  const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  for (const material of materials) {
    if (!isLightmapCapableMaterial(material)) {
      continue;
    }
    material.lightMap = lightMap;
    material.lightMapIntensity = entry.lightMapIntensity ?? 1.0;
    material.needsUpdate = true;
  }
}

export async function buildLightmapperMeshIndex(
  world: World,
  excludeActor?: Actor,
): Promise<Map<string, THREE.Mesh[]>> {
  const index = new Map<string, THREE.Mesh[]>();

  const addMeshes = (id: string, meshes: THREE.Mesh[]) => {
    if (meshes.length === 0) {
      return;
    }
    const existing = index.get(id);
    if (existing) {
      existing.push(...meshes);
    } else {
      index.set(id, meshes);
    }
  };

  for (const actor of world.getActors()) {
    if (actor === excludeActor) {
      continue;
    }

    for (const component of actor.getComponents(MeshComponent)) {
      if (component.isEditorOnly()) {
        continue;
      }
      addMeshes(lightmapperObjectId(component), [component.mesh]);
    }

    for (const component of actor.getComponents(ModelMeshComponent)) {
      if (component.isEditorOnly()) {
        continue;
      }
      await component.waitForLoad();
      addMeshes(lightmapperObjectId(component), component.getAllMeshes());
    }
  }

  return index;
}

async function applyManifestEntry(
  entry: LightmapManifestEntry,
  meshIndex: Map<string, THREE.Mesh[]>,
  buildData: LightmapBuildData,
  textures: SharedLightmapTextures,
  result: LightmapManifestApplyResult,
): Promise<void> {
  const meshes = meshIndex.get(entry.id);
  if (!meshes || meshes.length === 0) {
    result.skipped.push({ id: entry.id, reason: 'no matching MeshComponent/ModelMeshComponent in scene' });
    return;
  }

  const applicableMeshes = meshes.filter((mesh) => meshSupportsLightmap(mesh));
  if (applicableMeshes.length === 0) {
    result.skipped.push({ id: entry.id, reason: 'mesh material does not support light maps' });
    return;
  }

  try {
    for (const mesh of applicableMeshes) {
      await applyLightmap(mesh, buildData, entry, textures);
    }
    result.applied.push(entry.id);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    result.skipped.push({ id: entry.id, reason: message });
    console.error(`[lightmapApply] failed to apply lightmap for ${entry.id}:`, err);
  }
}

export async function applyManifestToWorld(
  world: World,
  sceneAssetPath: string,
  excludeActor?: Actor,
): Promise<LightmapManifestApplyResult> {
  const buildData = await loadLightmapBuildData(buildDataZipAssetPathForScene(sceneAssetPath));
  try {
    const meshIndex = await buildLightmapperMeshIndex(world, excludeActor);
    const textures = new SharedLightmapTextures();
    const result: LightmapManifestApplyResult = { applied: [], skipped: [] };

    for (const entry of buildData.manifest.objects) {
      await applyManifestEntry(entry, meshIndex, buildData, textures, result);
    }

    return result;
  } finally {
    buildData.dispose();
  }
}
